<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯方案' : '新增方案' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel" :disabled="loading">
          <i class="ri-close-line"></i>
          取消
        </button>
        <button type="button" class="btn btn-primary" :disabled="loading" @click="handleSubmit">
          <span v-if="loading">{{ isEdit ? '更新中...' : '建立中...' }}</span>
          <span v-else>{{ isEdit ? '更新方案' : '建立方案' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="create-plan-container">
    <div class="form-container">
      <form @submit.prevent="handleSubmit">
        <div class="form-section">
          <h3 class="section-title">基本資訊</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="planCode" class="required">方案代碼</label>
              <input
                id="planCode"
                v-model="form.planCode"
                type="text"
                class="form-control"
                :class="{ 
                  'is-invalid': errors.planCode, 
                  'is-valid': planCodeValidation.isValid && !isEdit,
                  'is-checking': planCodeValidation.isChecking
                }"
                placeholder="請輸入方案代碼（例如：BASIC, PRO, ENTERPRISE）"
                :disabled="isEdit"
                @input="handlePlanCodeInput"
                required
              />
              <div v-if="errors.planCode" class="invalid-feedback">
                {{ errors.planCode }}
              </div>
              <div v-else-if="planCodeValidation.isChecking && !isEdit" class="checking-feedback">
                <i class="ri-loader-4-line"></i> 檢查中...
              </div>
              <div v-else-if="planCodeValidation.isValid && !isEdit" class="valid-feedback">
                <i class="ri-checkbox-circle-line"></i> 此代碼可以使用
              </div>
                <p class="form-hint">方案代碼一旦建立後無法修改，輸入時會自動轉換為大寫，可使用字母、數字和底線</p>
            </div>

            <div class="form-group">
              <label for="planName" class="required">方案名稱</label>
              <input
                id="planName"
                v-model="form.planName"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.planName }"
                placeholder="請輸入方案名稱"
                required
              />
              <div v-if="errors.planName" class="invalid-feedback">
                {{ errors.planName }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="planType" class="required">方案類型</label>
              <select
                id="planType"
                v-model="form.planType"
                class="form-control"
                :class="{ 'is-invalid': errors.planType }"
                required
              >
                <option value="">請選擇方案類型</option>
                <option value="trial">試用版</option>
                <option value="formal">正式版</option>
                <option value="custom">客製版</option>
              </select>
              <div v-if="errors.planType" class="invalid-feedback">
                {{ errors.planType }}
              </div>
              <div class="form-hint-callout form-hint-callout--info" role="note">
                <div class="form-hint-callout__title">方案類型說明</div>
                <p class="form-hint-callout__body">
                  用於後台列表與辨識，不會自動開關功能；實際可用模組與上限請於本頁「功能限制」與模組設定調整。<br>
                  <strong>試用版</strong>：試用期方案分類，選取後可設定試用天數。<br>
                  <strong>正式版</strong>：一般付費或標準商務方案。<br>
                  <strong>客製版</strong>：專案談判、條件與標準方案不同時使用，方便日後辨識。
                </p>
              </div>
            </div>

            <div class="form-group">
              <label for="status">狀態</label>
              <select
                id="status"
                v-model="form.status"
                class="form-control"
              >
                <option value="active">啟用</option>
                <option value="inactive">停用</option>
              </select>
              <div class="form-hint-callout form-hint-callout--warning" role="note">
                <div class="form-hint-callout__title">狀態設為「停用」時的影響</div>
                <p class="form-hint-callout__body">
                  • 無法在客戶建立／編輯時被指定<br>
                  • 不會出現在公開 API 的方案列表中<br>
                  • 不會出現在客戶建立頁面的方案下拉選單中<br>
                  • <strong>已使用該方案的現有客戶不受影響，仍可正常使用</strong>
                </p>
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full-width">
              <label for="description">方案描述</label>
              <textarea
                id="description"
                v-model="form.description"
                class="form-control"
                rows="3"
                placeholder="請輸入方案描述（可選）"
                maxlength="1000"
              ></textarea>
              <p class="form-hint">{{ form.description?.length || 0 }}/1000 字元</p>
            </div>
          </div>
        </div>

        <!-- 試用天數（僅試用版顯示） -->
        <div v-if="form.planType === 'trial'" class="form-section">
          <h3 class="section-title">試用設定</h3>
          <div class="form-row">
            <div class="form-group">
              <label for="trialDays">試用天數</label>
              <input
                id="trialDays"
                v-model.number="form.trialDays"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.trialDays }"
                placeholder="請輸入試用天數（例如：14）"
                min="1"
                max="365"
              />
              <div v-if="errors.trialDays" class="invalid-feedback">{{ errors.trialDays }}</div>
              <p class="form-hint">新客戶申請試用時的有效天數，預設 14 天</p>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">價格設定</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="monthlyPrice">月費（NT$）</label>
              <input
                id="monthlyPrice"
                v-model.number="form.monthlyPrice"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.monthlyPrice }"
                placeholder="請輸入月費"
                min="0"
                step="0.01"
              />
              <div v-if="errors.monthlyPrice" class="invalid-feedback">
                {{ errors.monthlyPrice }}
              </div>
              <p class="form-hint">0 元為免費；空白代表『依方案報價』</p>
            </div>

            <div class="form-group">
              <label for="yearlyPrice">年費（NT$）</label>
              <input
                id="yearlyPrice"
                v-model.number="form.yearlyPrice"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.yearlyPrice }"
                placeholder="請輸入年費"
                min="0"
                step="0.01"
              />
              <div v-if="errors.yearlyPrice" class="invalid-feedback">
                {{ errors.yearlyPrice }}
              </div>
              <p class="form-hint">0 元為免費；空白代表『依方案報價』</p>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">功能限制</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="maxStaffAccounts">員工帳號上限</label>
              <input
                id="maxStaffAccounts"
                v-model.number="form.maxStaffAccounts"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.maxStaffAccounts }"
                placeholder="請輸入員工帳號上限（留空則不限制）"
                min="0"
              />
              <div v-if="errors.maxStaffAccounts" class="invalid-feedback">
                {{ errors.maxStaffAccounts }}
              </div>
              <p class="form-hint">留空則不限制員工帳號數量</p>
            </div>

            <div class="form-group">
              <label for="maxStores">門市數上限</label>
              <input
                id="maxStores"
                v-model.number="form.maxStores"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.maxStores }"
                placeholder="請輸入門市數上限（留空則不限制）"
                min="0"
              />
              <div v-if="errors.maxStores" class="invalid-feedback">
                {{ errors.maxStores }}
              </div>
              <p class="form-hint">留空則不限制門市數量</p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="maxMembers">會員數上限</label>
              <input
                id="maxMembers"
                v-model.number="form.maxMembers"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.maxMembers }"
                placeholder="請輸入會員數上限（留空則不限制）"
                min="0"
              />
              <div v-if="errors.maxMembers" class="invalid-feedback">
                {{ errors.maxMembers }}
              </div>
              <p class="form-hint">留空則不限制會員數量</p>
            </div>

            <div class="form-group">
              <label for="maxLineConfigs">LINE 官方帳號數量上限</label>
              <input
                id="maxLineConfigs"
                v-model.number="form.maxLineConfigs"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.maxLineConfigs }"
                placeholder="請輸入 LINE 官方帳號數量上限（留空則不限制）"
                min="0"
              />
              <div v-if="errors.maxLineConfigs" class="invalid-feedback">
                {{ errors.maxLineConfigs }}
              </div>
              <p class="form-hint">留空則不限制 LINE 官方帳號數量，預設值為 1</p>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title clickable" @click="showAdvancedFeatures = !showAdvancedFeatures">
            <i :class="showAdvancedFeatures ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"></i>
            進階功能
          </h3>
          
          <div v-show="showAdvancedFeatures" class="form-row">
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="form.allowApiAccess"
                  class="checkbox-input"
                />
                <span>允許 API 存取</span>
              </label>
              <p class="form-hint">啟用後允許透過 API 存取系統功能</p>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="form.allowPosIntegration"
                  class="checkbox-input"
                />
                <span>允許 POS 系統整合</span>
              </label>
              <p class="form-hint">啟用後允許與 POS 系統進行整合</p>
            </div>
          </div>

          <div v-show="showAdvancedFeatures" class="form-row">
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="form.isDefault"
                  class="checkbox-input"
                />
                <span>設為預設方案</span>
              </label>
              <p class="form-hint">預設方案將作為新客戶的預設選擇，設定後將清除舊的預設方案</p>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="form.isSelfSubscribable"
                  class="checkbox-input"
                />
                <span>開放客戶自行訂閱</span>
              </label>
              <p class="form-hint">開啟後，此方案會出現在客戶訂閱頁面供客戶自行選購；關閉則僅系統管理員可指定</p>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="form.showOnWebsite"
                  class="checkbox-input"
                />
                <span>顯示於官網</span>
              </label>
              <p class="form-hint">關閉後，官網公開方案 API 不會列出此方案（仍須方案為啟用，且依頁面篩選「開放客戶自行訂閱」等）</p>
            </div>
          </div>
        </div>

        <ModuleSelector
          v-model="selectedModuleIds"
          title="模組設定"
          description="選擇此方案包含的功能模組。客戶使用此方案時，只能使用已勾選的模組。"
          :modules="availableModules"
          :loading="loadingModules"
          :error="modulesError"
          @reload="fetchModules"
        />

        <div v-if="errorMessage" class="alert alert-danger">
          {{ errorMessage }}
        </div>

      </form>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { planService } from '@/api/modules/plans.js';
import { planModuleService } from '@/api/modules/planModules.js';
import { moduleService } from '@/api/modules/modules.js';
import { showSuccess, showError } from '@/utils/swal.js';
import ModuleSelector from '@/components/ModuleSelector.vue';

export default {
  name: 'CreatePlan',
  components: {
    ModuleSelector
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const errorMessage = ref('');
    const errors = reactive({});
    const planCodeCheckTimeout = ref(null);
    const planCodeValidation = reactive({
      isChecking: false,
      isValid: false,
      message: ''
    });

    // 模組相關狀態
    const loadingModules = ref(false);
    const modulesError = ref('');
    const availableModules = ref([]);
    const selectedModuleIds = ref([]);

    // 進階功能展開/收起狀態
    const showAdvancedFeatures = ref(false);

    const isEdit = computed(() => {
      return !!route.params.id;
    });

    /** 價格欄位：清空送 null（依方案報價）；0 為免費，須保留數字 0 */
    function parseOptionalPrice(val) {
      if (val === '' || val === null || val === undefined) return null;
      if (typeof val === 'number' && Number.isNaN(val)) return null;
      const n = Number(val);
      if (Number.isNaN(n)) return null;
      return n;
    }

    const form = reactive({
      planCode: '',
      planName: '',
      planType: '',
      description: '',
      monthlyPrice: null,
      yearlyPrice: null,
      maxStaffAccounts: null,
      maxStores: null,
      maxMembers: null,
      maxLineConfigs: 1, // 預設值為 1
      allowApiAccess: false,
      allowPosIntegration: false,
      trialDays: 14,
      status: 'active',
      isDefault: false,
      isSelfSubscribable: false,
      showOnWebsite: true,
    });

    const validateForm = () => {
      // 清除之前的錯誤
      Object.keys(errors).forEach(key => delete errors[key]);
      errorMessage.value = '';

      let isValid = true;

      // 驗證方案代碼
      if (!isEdit.value && (!form.planCode || form.planCode.trim().length < 1)) {
        errors.planCode = '請輸入方案代碼';
        isValid = false;
      } else if (!isEdit.value && !/^[A-Za-z0-9_]+$/.test(form.planCode)) {
        errors.planCode = '方案代碼只能包含大小寫字母、數字和底線';
        isValid = false;
      }

      // 驗證方案名稱
      if (!form.planName || form.planName.trim().length === 0) {
        errors.planName = '請輸入方案名稱';
        isValid = false;
      }

      // 驗證方案類型
      if (!form.planType) {
        errors.planType = '請選擇方案類型';
        isValid = false;
      }

      // 驗證價格（若有有效數字，必須大於等於 0）
      const mp = parseOptionalPrice(form.monthlyPrice);
      if (mp !== null && mp < 0) {
        errors.monthlyPrice = '月費不能為負數';
        isValid = false;
      }

      const yp = parseOptionalPrice(form.yearlyPrice);
      if (yp !== null && yp < 0) {
        errors.yearlyPrice = '年費不能為負數';
        isValid = false;
      }

      // 驗證限制數量（如果提供了值，必須大於等於 0）
      if (form.maxStaffAccounts !== null && form.maxStaffAccounts !== undefined && form.maxStaffAccounts !== '' && form.maxStaffAccounts < 0) {
        errors.maxStaffAccounts = '員工帳號上限不能為負數';
        isValid = false;
      }

      if (form.maxStores !== null && form.maxStores !== undefined && form.maxStores !== '' && form.maxStores < 0) {
        errors.maxStores = '門市數上限不能為負數';
        isValid = false;
      }

      if (form.maxMembers !== null && form.maxMembers !== undefined && form.maxMembers !== '' && form.maxMembers < 0) {
        errors.maxMembers = '會員數上限不能為負數';
        isValid = false;
      }

      if (form.maxLineConfigs !== null && form.maxLineConfigs !== undefined && form.maxLineConfigs !== '' && form.maxLineConfigs < 0) {
        errors.maxLineConfigs = 'LINE 官方帳號數量上限不能為負數';
        isValid = false;
      }

      return isValid;
    };

    const checkPlanCodeAvailability = async (planCode) => {
      // 重置驗證狀態
      planCodeValidation.isChecking = true;
      planCodeValidation.isValid = false;
      planCodeValidation.message = '';
      delete errors.planCode;

      try {
        // 先進行格式驗證
        if (!planCode || planCode.trim().length === 0) {
          planCodeValidation.isChecking = false;
          return;
        }

        if (!/^[A-Za-z0-9_]+$/.test(planCode)) {
          errors.planCode = '方案代碼只能包含大小寫字母、數字和底線';
          planCodeValidation.isChecking = false;
          return;
        }

        // 呼叫後端 API 檢查（後端要求大寫）
        const response = await planService.checkPlanCode(planCode.toUpperCase());
        
        if (response && response.success && response.data) {
          if (response.data.available) {
            planCodeValidation.isValid = true;
            planCodeValidation.message = '此代碼可以使用';
          } else {
            errors.planCode = '此方案代碼已被使用，請使用其他代碼';
            planCodeValidation.isValid = false;
          }
        }
      } catch (error) {
        console.error('檢查方案代碼失敗:', error);
        // 檢查失敗時不顯示錯誤，讓用戶可以繼續輸入
      } finally {
        planCodeValidation.isChecking = false;
      }
    };

    const handlePlanCodeInput = (event) => {
      // 自動轉換為大寫
      const upperValue = event.target.value.toUpperCase();
      form.planCode = upperValue;

      // 清除之前的計時器
      if (planCodeCheckTimeout.value) {
        clearTimeout(planCodeCheckTimeout.value);
      }

      // 重置驗證狀態
      planCodeValidation.isValid = false;
      delete errors.planCode;

      const planCode = form.planCode.trim();

      // 如果輸入為空，直接返回
      if (!planCode) {
        planCodeValidation.isChecking = false;
        return;
      }

      // 設置防抖延遲（500ms）
      planCodeCheckTimeout.value = setTimeout(() => {
        checkPlanCodeAvailability(planCode.toUpperCase());
      }, 500);
    };

    // 載入所有可用模組
    const fetchModules = async () => {
      loadingModules.value = true;
      modulesError.value = '';

      try {
        const response = await moduleService.getModules({
          page: 1,
          pageSize: 100,
          status: 'active'
        });

        if (response && response.success && response.data) {
          availableModules.value = response.data.items || [];
        } else {
          modulesError.value = '無法載入模組列表';
        }
      } catch (error) {
        console.error('載入模組列表失敗:', error);
        modulesError.value = '載入模組列表失敗，請稍後再試';
      } finally {
        loadingModules.value = false;
      }
    };

    // 載入方案已關聯的模組
    const fetchPlanModules = async (planId) => {
      try {
        const response = await planModuleService.getModulesByPlanId(planId);

        if (response && response.success && response.data) {
          // 提取已關聯的模組 ID
          // 後端回應的資料結構是 moduleId，不是 id
          selectedModuleIds.value = (response.data || []).map(m => m.moduleId);
          console.log('✅ 載入方案模組成功，已選擇:', selectedModuleIds.value.length, '個模組');
        }
      } catch (error) {
        console.error('載入方案模組失敗:', error);
        // 不顯示錯誤，允許用戶繼續編輯
      }
    };

    // 儲存方案模組關聯
    const savePlanModules = async (planId) => {
      try {
        await planModuleService.updatePlanModules(planId, selectedModuleIds.value);
        return true;
      } catch (error) {
        console.error('儲存方案模組失敗:', error);
        throw error;
      }
    };

    const fetchPlan = async () => {
      if (!isEdit.value) {
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      try {
        const response = await planService.getPlan(route.params.id);

        if (response && response.success && response.data) {
          const plan = response.data;
          form.planCode = plan.planCode || '';
          form.planName = plan.planName || '';
          form.planType = plan.planType || '';
          form.description = plan.description || '';
          form.monthlyPrice = plan.monthlyPrice ?? null;
          form.yearlyPrice = plan.yearlyPrice ?? null;
          form.maxStaffAccounts = plan.maxStaffAccounts || null;
          form.maxStores = plan.maxStores || null;
          form.maxMembers = plan.maxMembers || null;
          form.maxLineConfigs = plan.maxLineConfigs !== undefined && plan.maxLineConfigs !== null ? plan.maxLineConfigs : 1;
          form.allowApiAccess = plan.allowApiAccess || false;
          form.allowPosIntegration = plan.allowPosIntegration || false;
          form.trialDays = plan.trialDays || 14;
          form.status = plan.status || 'active';
          form.isDefault = plan.isDefault || false;
          form.isSelfSubscribable = plan.isSelfSubscribable || false;
          form.showOnWebsite = plan.showOnWebsite !== false;

          // 載入模組列表和已關聯的模組
          await fetchModules();
          await fetchPlanModules(route.params.id);
        } else {
          await showError('獲取失敗', response && response.message || '獲取方案資料失敗');
          router.push({ name: 'PlanList' });
        }
      } catch (error) {
        console.error('獲取方案資料失敗:', error);
        let errorMessage = '獲取方案資料失敗，請稍後再試';
        if (error.code === 'PLAN_NOT_FOUND_001' || error.code === 'NOT_FOUND') {
          errorMessage = '方案不存在或已被刪除';
        } else if (error.response?.status === 404) {
          errorMessage = '方案不存在或路由錯誤';
        } else if (error.response?.status === 403) {
          errorMessage = '您沒有權限查看此方案';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        await showError('獲取失敗', errorMessage);
        router.push({ name: 'PlanList' });
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      try {
        const planData = {
          planName: form.planName.trim(),
          planType: form.planType,
          status: form.status,
          allowApiAccess: form.allowApiAccess,
          allowPosIntegration: form.allowPosIntegration,
          isDefault: form.isDefault,
          isSelfSubscribable: form.isSelfSubscribable,
          showOnWebsite: form.showOnWebsite,
        };

        // 新增時才需要 planCode（後端要求大寫）
        if (!isEdit.value) {
          planData.planCode = form.planCode.trim().toUpperCase();
        }

        // 只有當有值時才加入
        if (form.description) {
          planData.description = form.description.trim();
        }
        planData.monthlyPrice = parseOptionalPrice(form.monthlyPrice);
        planData.yearlyPrice = parseOptionalPrice(form.yearlyPrice);
        
        // 數字欄位：如果是空字串或 null/undefined，則傳 null（表示清空），否則傳數字
        planData.maxStaffAccounts = (form.maxStaffAccounts === '' || form.maxStaffAccounts === null || form.maxStaffAccounts === undefined) 
          ? null 
          : Number(form.maxStaffAccounts);
        planData.maxStores = (form.maxStores === '' || form.maxStores === null || form.maxStores === undefined) 
          ? null 
          : Number(form.maxStores);
        planData.maxMembers = (form.maxMembers === '' || form.maxMembers === null || form.maxMembers === undefined) 
          ? null 
          : Number(form.maxMembers);
        planData.maxLineConfigs = (form.maxLineConfigs === '' || form.maxLineConfigs === null || form.maxLineConfigs === undefined) 
          ? null 
          : Number(form.maxLineConfigs);

        if (form.planType === 'trial') {
          planData.trialDays = form.trialDays ? Number(form.trialDays) : 14;
        }

        let response;
        if (isEdit.value) {
          response = await planService.updatePlan(route.params.id, planData);
          
          // 編輯模式：同時儲存模組關聯
          if (response && response.success) {
            try {
              await savePlanModules(route.params.id);
            } catch (moduleError) {
              console.error('儲存模組關聯失敗:', moduleError);
              await showError('警告', '方案更新成功，但模組關聯儲存失敗。請稍後重試。');
              router.push({ name: 'PlanList' });
              return;
            }
          }
        } else {
          response = await planService.createPlan(planData);
          
          // 新增模式：同時儲存模組關聯
          if (response && response.success && response.data && response.data.id) {
            try {
              await savePlanModules(response.data.id);
            } catch (moduleError) {
              console.error('儲存模組關聯失敗:', moduleError);
              await showError('警告', '方案建立成功，但模組關聯儲存失敗。請稍後重試。');
              router.push({ name: 'PlanList' });
              return;
            }
          }
        }

        if (response && response.success) {
          await showSuccess(
            isEdit.value ? '更新成功' : '新增成功',
            `方案「${planData.planName}」已成功${isEdit.value ? '更新' : '建立'}`
          );
          // 返回列表頁，如果有 returnPage 參數則恢復頁碼
          const returnPage = route.query.returnPage;
          if (returnPage) {
            router.push({ name: 'PlanList', query: { page: returnPage } });
          } else {
            router.push({ name: 'PlanList' });
          }
        } else {
          let errorTitle = isEdit.value ? '更新失敗' : '新增失敗';
          let errorText = response?.message || `${isEdit.value ? '更新' : '新增'}方案失敗`;

          if (response?.error?.code === 'PLAN_CONFLICT_001') {
            errors.planCode = '方案代碼已存在';
            errorText = '方案代碼已存在';
          }
          await showError(errorTitle, errorText);
        }
      } catch (error) {
        console.error(`${isEdit.value ? '更新' : '新增'}方案失敗:`, error);
        let errorMessage = `${isEdit.value ? '更新' : '新增'}方案失敗，請稍後再試`;
        if (error.code === 'PLAN_CONFLICT_001') {
          errorMessage = '方案代碼已存在，請使用其他代碼';
        } else if (error.code === 'PLAN_NOT_FOUND_001' || error.code === 'NOT_FOUND') {
          errorMessage = '方案不存在或已被刪除';
        } else if (error.code === 'FORBIDDEN') {
          errorMessage = '您沒有權限執行此操作';
        } else if (error.response?.status === 401) {
          errorMessage = '未授權，請重新登入';
        } else if (error.response?.status === 403) {
          errorMessage = '您沒有權限執行此操作';
        } else if (error.response?.status === 400) {
          errorMessage = error.response?.data?.message || '輸入資料驗證失敗，請檢查輸入內容';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        await showError(
          isEdit.value ? '更新失敗' : '新增失敗',
          errorMessage
        );
      } finally {
        loading.value = false;
      }
    };

    const handleCancel = () => {
      // 返回列表頁，如果有 returnPage 參數則恢復頁碼
      const returnPage = route.query.returnPage;
      if (returnPage) {
        router.push({ name: 'PlanList', query: { page: returnPage } });
      } else {
        router.push({ name: 'PlanList' });
      }
    };

    onMounted(() => {
      if (isEdit.value) {
        fetchPlan();
      } else {
        // 新增模式下也載入模組列表
        fetchModules();
      }
    });

    return {
      form,
      errors,
      errorMessage,
      loading,
      isEdit,
      planCodeValidation,
      handlePlanCodeInput,
      handleSubmit,
      handleCancel,
      // 模組相關
      loadingModules,
      modulesError,
      availableModules,
      selectedModuleIds,
      fetchModules,
      // 進階功能
      showAdvancedFeatures,
    };
  },
};
</script>

<style scoped>
.create-plan-container {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.form-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
}

.section-title.clickable {
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: color 0.2s;
}

.section-title.clickable:hover {
  color: #667eea;
}

.section-title.clickable i {
  font-size: 20px;
  transition: transform 0.2s;
}

.section-description {
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.6;
}

/* 模組載入狀態 */
.loading-modules {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  color: #666;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.modules-error {
  text-align: center;
  padding: 40px;
}

.modules-error .error-message {
  color: #dc3545;
  margin-bottom: 16px;
}

/* 模組網格 */

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: normal;
}

.checkbox-input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.form-control {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
  font-family: inherit;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.form-control.is-valid {
  border-color: #28a745;
  padding-right: 2.5rem;
}

.form-control.is-checking {
  border-color: #ffc107;
}

textarea.form-control {
  resize: vertical;
  min-height: 80px;
}

.invalid-feedback {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.valid-feedback {
  color: #28a745;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.valid-feedback i {
  font-size: 14px;
}

.checking-feedback {
  color: #ffc107;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.checking-feedback i {
  font-size: 14px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.form-hint {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  margin-bottom: 0;
}

.form-hint-callout {
  margin-top: 10px;
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  border-left: 5px solid transparent;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.form-hint-callout--info {
  border-color: #9ec5fe;
  background: linear-gradient(145deg, #e7f1ff 0%, #f4f9ff 100%);
  border-left-color: #0d6efd;
  box-shadow: 0 1px 3px rgba(13, 110, 253, 0.08);
}

.form-hint-callout--warning {
  border-color: #ffe69c;
  background: linear-gradient(145deg, #fff8e7 0%, #fffbf0 100%);
  border-left-color: #e0a800;
  box-shadow: 0 1px 3px rgba(224, 168, 0, 0.12);
}

.form-hint-callout__title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
  letter-spacing: 0.02em;
}

.form-hint-callout--info .form-hint-callout__title {
  color: #084298;
}

.form-hint-callout--warning .form-hint-callout__title {
  color: #664d03;
}

.form-hint-callout__body {
  font-size: 13px;
  line-height: 1.6;
  margin: 0;
}

.form-hint-callout--info .form-hint-callout__body {
  color: #1a3a5c;
}

.form-hint-callout--info .form-hint-callout__body strong {
  color: #052c65;
}

.form-hint-callout--warning .form-hint-callout__body {
  color: #523203;
}

.form-hint-callout--warning .form-hint-callout__body strong {
  color: #3d2500;
}

.alert {
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.alert-danger {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #5a6268;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
